import { 
    specStore, 
    claimsStore, 
    termsStore 
} from '../stores/editorsStore.js';

/**
 * Custom error class for store initialization failures
 */
export class StoreInitializationError extends Error {
    constructor(storeName, originalError) {
        super(`Failed to initialize ${storeName} store: ${originalError.message}`);
        this.name = 'StoreInitializationError';
        this.storeName = storeName;
        this.originalError = originalError;
    }
}

/**
 * Service for initializing application stores with default values
 */
export class StoreInitializationService {
    #initialized = false;
    #initializationErrors = [];

    /**
     * Initialize a single store with error handling
     * @param {string} storeName - Name of the store for error reporting
     * @param {Function} initFn - Function to initialize the store
     * @private
     * @throws {StoreInitializationError} If store initialization fails
     */
    #initializeStore(storeName, initFn) {
        try {
            initFn();
        } catch (error) {
            const storeError = new StoreInitializationError(storeName, error);
            this.#initializationErrors.push(storeError);
            throw storeError;
        }
    }

    /**
     * Initialize all stores with their default values.
     * This is idempotent - calling it multiple times will only initialize once.
     * @throws {StoreInitializationError} If any store fails to initialize
     * @returns {boolean} True if initialization was successful
     */
    initialize() {
        if (this.#initialized) {
            return true;
        }

        // Clear any previous errors
        this.#initializationErrors = [];

        try {
            // Initialize spec editor store
            this.#initializeStore('spec', () => {
                specStore.set({
                    content: '',
                    protectedRanges: [],
                    isRegenerating: false
                });
            });
            
            // Initialize claims editor store
            this.#initializeStore('claims', () => {
                claimsStore.set({
                    content: '# This is an in-line comment. It won\'t appear in the exported Word document.\n1. ',
                    terms: new Set(),
                    antecedentErrors: []
                });
            });
            
            // Initialize terms panel store
            this.#initializeStore('terms', () => {
                termsStore.set({
                    termEntries: new Map(),
                    selectedEntries: new Set(),
                    highlightedEntries: new Set(),
                    expandedEntries: new Set(),
                    editingEntry: null,
                    isTermAddingMode: false,
                    manualTerms: new Set(),
                    preservedTerms: new Set(),
                    isAnalyzingTerms: false,
                    loadingOperations: new Map(),
                    lastRefreshType: null,
                    refreshStateSummary: null
                });
            });

            this.#initialized = true;
            return true;
        } catch (error) {
            // If any store fails to initialize, we'll try to reset the ones that did
            // But we won't clear the errors since we want to preserve them for reporting
            this.reset();
            throw error;
        }
    }

    /**
     * Check if stores have been initialized
     */
    get isInitialized() {
        return this.#initialized;
    }

    /**
     * Get any errors that occurred during initialization
     */
    get errors() {
        return [...this.#initializationErrors];
    }

    /**
     * Reset all stores to their default values
     * This will work even if stores were already initialized
     * @returns {boolean} True if reset was successful
     */
    reset() {
        this.#initialized = false;
        
        try {
            // Attempt to clear all stores
            specStore.set({ content: '', protectedRanges: [], isRegenerating: false });
            claimsStore.set({ content: '# This is an in-line comment. It won\'t appear in the exported Word document.\n1. ', terms: new Set(), antecedentErrors: [] });
            termsStore.set({ allTerms: new Set(), selectedTerms: new Set(), highlightedTerms: new Set() });
            return true;
        } catch (error) {
            console.error('Error resetting stores:', error);
            return false;
        }
    }
}

export const storeInitializationService = new StoreInitializationService();
